import { assertNever } from './exhaustive';
import { alignBucketStart, type DateGranularity, nextBucketStart } from './record-group';
import type { ReportMetricOp, ReportRange } from './report';

/**
 * THE GAP THE SERVER DOES NOT FILL (`docs/decisions/reports.md` T4,
 * `group-by-aggregation.md:1415`).
 *
 * A `GROUP BY` emits no row for a group with no rows. That is correct SQL and it is a
 * lie on a line chart: a month with no closed deals is a MISSING BUCKET, and a line drawn
 * straight from the response closes the gap by joining February to April as if March had
 * never been asked about. The reader sees a smooth slope where the truth is a hole.
 *
 * So the completion happens here, from the range the caller ASKED for rather than from
 * the data that came back - which is the only way to know that March was in the question.
 * It is deliberately not on the server: the server answers what it was asked, and
 * synthesising rows it did not read would make the response disagree with the same query
 * run against psql (D28's argument for the fixture client, applied to the real one).
 *
 * WHAT A MISSING BUCKET IS WORTH depends on the metric, and getting this wrong is the
 * quiet bug this helper exists to prevent:
 *  - `count` and `sum`: ZERO. No deals closed in March means the count is nought and the
 *    sum of nothing is nought. Both are real quantities and belong on the axis.
 *  - `avg`, `min` and `max`: NULL. There is no average of no numbers. Filling a zero here
 *    would draw a line to the floor and claim March's average contract was worth nothing,
 *    which is a different and wronger statement than "March had none".
 */
export function emptyBucketValue(op: ReportMetricOp): number | null {
  switch (op) {
    case 'count':
    case 'sum':
      return 0;
    case 'avg':
    case 'min':
    case 'max':
      return null;
    default:
      return assertNever(op, 'emptyBucketValue');
  }
}

/** One bucket of a time series: the bucket's start date and what it is worth. */
export interface SeriesBucket {
  /** The bucket key, a `YYYY-MM-DD` wall-clock date, exactly as the group read returns. */
  readonly bucketStart: string;
  readonly value: number | null;
}

export interface FillOptions {
  readonly granularity: DateGranularity;
  /** Inclusive, any date inside the first bucket. */
  readonly from: string;
  /** EXCLUSIVE, any date inside the bucket after the last one. */
  readonly to: string;
  readonly op: ReportMetricOp;
}

/**
 * Every bucket the range covers, in calendar order, with the values that were read and
 * `emptyBucketValue` for the ones that were not.
 *
 * BUCKETS OUTSIDE THE RANGE ARE KEPT, not dropped. A read can legitimately return one -
 * a filter the caller composed by hand, a record dated in the future - and silently
 * discarding a number the database really returned is worse than showing a chart one
 * bucket wider than the range asked for. They are merged in calendar order like the rest.
 */
export function fillBuckets(
  buckets: readonly SeriesBucket[],
  options: FillOptions,
): readonly SeriesBucket[] {
  const { granularity, op } = options;
  const read = new Map<string, number | null>();
  for (const bucket of buckets) read.set(bucket.bucketStart, bucket.value);

  const wanted: string[] = [];
  /**
   * THE LAST BUCKET IS THE ONE CONTAINING THE DAY BEFORE `to`, not the one `to` aligns to.
   *
   * `to` is exclusive, and aligning it DOWN was wrong in a way the tests caught: with
   * `to = 2026-09-30` and quarterly buckets, aligning gives Q3's start and the loop then
   * stops BEFORE Q3 - dropping a quarter the range covers almost all of. Stepping back one
   * day first gets both cases right, the clean boundary a resolved window always produces
   * (`to = 2026-10-01` still ends at Q3) and the hand-passed date in the middle of a bucket.
   */
  const last = alignBucketStart(previousDay(options.to), granularity);
  let cursor = alignBucketStart(options.from, granularity);
  // A range that ends before it starts covers nothing; the loop simply does not run.
  while (cursor <= last) {
    wanted.push(cursor);
    const next = nextBucketStart(cursor, granularity);
    // Defensive: the calendar always moves forward, and a helper that did not would hang
    // the browser rather than fail. Cheap to assert, impossible to debug from a frozen tab.
    if (next <= cursor) throw new Error(`bucket ${cursor} does not advance under ${granularity}`);
    cursor = next;
  }

  const extra = [...read.keys()].filter((key) => !wanted.includes(key));
  const all = [...wanted, ...extra].sort();
  return all.map((bucketStart) => ({
    bucketStart,
    value: read.has(bucketStart) ? (read.get(bucketStart) ?? null) : emptyBucketValue(op),
  }));
}

/** The calendar day before an ISO date. Used to turn an exclusive end into an inclusive one. */
function previousDay(isoDate: string): string {
  const parsed = Date.parse(`${isoDate}T00:00:00.000Z`);
  if (Number.isNaN(parsed)) throw new Error(`"${isoDate}" is not a date that exists`);
  return new Date(parsed - 86_400_000).toISOString().slice(0, 10);
}

/** A resolved window: `from` inclusive, `to` exclusive, both `YYYY-MM-DD`. */
export interface ReportWindow {
  readonly from: string;
  readonly to: string;
}

const DAY_MS = 86_400_000;

function iso(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

function utcMidnight(year: number, month: number, day: number): number {
  return Date.UTC(year, month, day);
}

/**
 * THE FIVE RANGES (R3), resolved against a wall-clock day.
 *
 * `to` IS EXCLUSIVE THROUGHOUT, which is the only convention under which "this quarter"
 * and "last 12 months" can both be written without an off-by-one: the window ends at the
 * start of the next bucket rather than at "the last day", and no code has to know how
 * many days February had.
 *
 * `all_time` returns null rather than a very wide window. A window covering the year 1970
 * would put 600 empty months on a chart; "no window" is a different answer and the chart
 * draws exactly the buckets the data has.
 *
 * The reference day is passed in rather than read from the clock, so a test can state
 * which day it is and the answer is not different in December.
 */
export function resolveReportWindow(range: ReportRange, today: Date): ReportWindow | null {
  const year = today.getUTCFullYear();
  const month = today.getUTCMonth();
  const day = today.getUTCDate();
  const midnight = utcMidnight(year, month, day);

  switch (range) {
    case 'all_time':
      return null;
    /** Thirty calendar days ENDING TODAY, so today is the thirtieth and not the thirty-first. */
    case 'last_30_days':
      return { from: iso(midnight - 29 * DAY_MS), to: iso(midnight + DAY_MS) };
    case 'this_quarter': {
      const quarterMonth = month - (month % 3);
      return {
        from: iso(utcMidnight(year, quarterMonth, 1)),
        to: iso(utcMidnight(year, quarterMonth + 3, 1)),
      };
    }
    /** Twelve WHOLE months including the current one, so the current month is the twelfth. */
    case 'last_12_months':
      return {
        from: iso(utcMidnight(year, month - 11, 1)),
        to: iso(utcMidnight(year, month + 1, 1)),
      };
    case 'this_year':
      return { from: iso(utcMidnight(year, 0, 1)), to: iso(utcMidnight(year + 1, 0, 1)) };
    default:
      return assertNever(range, 'resolveReportWindow');
  }
}
